import { standingsApi } from '@/api/endpoints';
import type { Standings } from '@/api/types';
import { useAuth } from '@/auth/AuthContext';
import { Alert, Card, Spinner } from '@/components/ui';
import { truncateForOverview } from '@/lib/text';
import { useAsync } from '@/lib/useAsync';
import adminStyles from '@/features/admin/admin.module.css';
import styles from './standings.module.css';

/** Siegpunkte als x,xx (z. B. 1,50) in deutscher Schreibweise. */
function formatSiegpunkte(value: number | undefined): string {
  return (value ?? 0).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Gesamttabelle — kicktipp-Matrix: eine Spalte je Spieltag mit den dort erzielten
 * Punkten, Spieltagssieger (Höchstpunktzahl > 0) hervorgehoben, plus B (Bonus),
 * S (Siegpunkte) und G (Gesamt) am Ende. Sortiert nach Gesamtpunkten.
 */
export function StandingsPage() {
  const { user } = useAuth();
  const { data, loading, error } = useAsync<Standings>((signal) => standingsApi.overall(signal), [], {
    refreshOnFocus: true,
  });

  if (loading) return <Spinner label="Tabelle wird geladen…" />;
  if (error) return <Alert>{error}</Alert>;
  if (!data) return <Alert>Keine Daten.</Alert>;

  const { matchdays, standings } = data;

  // Spieltagssieger je Spalte: höchste Punktzahl dieser Spalte, sofern > 0.
  const columnMax = matchdays.map((_, i) =>
    Math.max(0, ...standings.map((entry) => entry.matchday_points[i] ?? 0)),
  );

  return (
    <>
      <h1>Gesamtübersicht</h1>
      <p className="muted">
        Punkte je Spieltag. Spieltagssieger sind hervorgehoben. B = Bonus, S = Siegpunkte, G =
        Gesamt.
      </p>

      {standings.length === 0 ? (
        <Card>
          <p className="muted">Noch keine Wertung vorhanden.</p>
        </Card>
      ) : (
        <Card>
          <div style={{ overflowX: 'auto' }}>
            <table className={`${adminStyles.rankTable} ${styles.standingsTable}`}>
              <thead>
                <tr>
                  <th className={adminStyles.rankPos}>#</th>
                  <th>Name</th>
                  {/* Laufende Nummer statt sort_order: der ist nur je Wettbewerb
                      eindeutig (Liga 1..38 vs. Pokal 1..7) und wäre doppelt. */}
                  {matchdays.map((md, mdIndex) => (
                    <th key={md.id} className={styles.mdCol} title={md.name}>
                      {mdIndex + 1}
                    </th>
                  ))}
                  <th className={adminStyles.rankTotal}>B</th>
                  <th className={adminStyles.rankTotal}>S</th>
                  <th className={adminStyles.rankTotal}>G</th>
                </tr>
              </thead>
              <tbody>
                {standings.map((entry, i) => (
                  <tr
                    key={entry.user_id}
                    className={entry.user_id === user?.id ? adminStyles.rankSelf : undefined}
                  >
                    <td className={adminStyles.rankPos}>{i + 1}</td>
                    <td className={adminStyles.rankName}>
                      {truncateForOverview(entry.display_name)}
                      {entry.kackhaufen ? ' 💩' : ''}
                    </td>
                    {matchdays.map((md, mdIndex) => {
                      const pts = entry.matchday_points[mdIndex] ?? 0;
                      const max = columnMax[mdIndex] ?? 0;
                      const isWinner = max > 0 && pts === max;
                      return (
                        <td key={md.id} className={isWinner ? styles.mdWinner : styles.mdCol}>
                          {pts}
                        </td>
                      );
                    })}
                    <td className={adminStyles.rankTotal}>{entry.bonus ?? 0}</td>
                    <td className={adminStyles.rankTotal}>{formatSiegpunkte(entry.siegpunkte)}</td>
                    <td className={adminStyles.rankTotal}>{entry.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}
